/**
 * Redux state type definitions for Five Nights at Khan's
 */

// Game states
export type GameState = 'menu' | 'playing' | 'paused' | 'gameOver' | 'victory';
export type Difficulty = 'easy' | 'medium' | 'hard';

// Objective data for Redux state
export interface ObjectiveData {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  required: boolean;
  hidden: boolean;
  currentValue?: number;
  targetValue?: number;
}

// Game slice state
export interface GameSliceState {
  gameState: GameState;
  currentNight: number;
  difficulty: Difficulty;
  isPaused: boolean;
  isLoading: boolean;
  timeElapsed: number; // in seconds
  nightDuration: number; // in seconds
  objectives: ObjectiveData[]; // Current night objectives
}

// Player slice state
export interface PlayerSliceState {
  // Stats
  health: number;
  maxHealth: number;
  stamina: number;
  maxStamina: number;
  speed: number; // base speed multiplier
  level: number;
  xp: number;
  xpToNextLevel: number;

  // Position (will be synced from game engine)
  position: { x: number; y: number };
  velocity: { x: number; y: number };

  // Equipment/items
  flashlightBattery: number; // 0-100
  hasMask: boolean;
  isUsingMask: boolean;
  isUsingFlashlight: boolean;

  // State
  isAlive: boolean;
  isHiding: boolean;
  isCrouching: boolean;
  isSprinting: boolean;
}

// UI slice state
export interface UISliceState {
  showHUD: boolean;
  showMenu: boolean;
  showPauseMenu: boolean;
  showUpgradeMenu: boolean;
  showSettingsMenu: boolean;
  showObjectives: boolean;
  showMinimap: boolean;
  currentMenu: string | null;
  notifications: Notification[];
}

export interface Notification {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  duration: number; // in milliseconds
  timestamp: number;
}

// Settings slice state
export interface SettingsSliceState {
  // Audio
  masterVolume: number; // 0-100
  musicVolume: number; // 0-100
  sfxVolume: number; // 0-100

  // Graphics
  fullscreen: boolean;
  vsync: boolean;
  showFPS: boolean;
  postProcessing: boolean;
  bloom: boolean;
  screenShake: boolean;

  // Controls
  keyBindings: KeyBindings;
  mouseSensitivity: number; // 0-100

  // Gameplay
  enableTutorials: boolean;
  showDamageNumbers: boolean;
  autoSave: boolean;
}

export interface KeyBindings {
  moveLeft: string;
  moveRight: string;
  jump: string;
  crouch: string;
  sprint: string;
  interact: string;
  flashlight: string;
  mask: string;
  pause: string;
  inventory: string;
}

// Default values
export const DEFAULT_KEY_BINDINGS: KeyBindings = {
  moveLeft: 'KeyA',
  moveRight: 'KeyD',
  jump: 'Space',
  crouch: 'ControlLeft',
  sprint: 'ShiftLeft',
  interact: 'KeyE',
  flashlight: 'KeyF',
  mask: 'KeyQ',
  pause: 'Escape',
  inventory: 'KeyI',
};
